import apiClient from './apiClient';
import { ManagedAccount, ApiResponse } from '../types';

const API_TYPE = 'api' as const;

class ManagedAccountsService {
  /** SuperAdmin-only attach. Assigns a managed Zebu user to a target
   *  AccountManager's pool. After Phase 6's hard switch, this endpoint
   *  rejects calls from non-SuperAdmins with 403. The relation row's
   *  admin_user_id is set to accountManagerUserId; granted_by captures
   *  the SuperAdmin caller. */
  async attach(
    accountManagerUserId: string,
    managedClientId: string,
  ): Promise<ManagedAccount> {
    try {
      const response = await apiClient.post<ApiResponse<ManagedAccount>>(
        '/admin/managed-users',
        {
          account_manager_user_id: accountManagerUserId,
          managed_client_id: managedClientId,
        },
        undefined,
        API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to attach managed account');
      }
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to attach managed account',
      );
    }
  }

  /** List managed pool. Without `accountManagerUserId`, returns the caller's
   *  own pool. With it, scopes to that AM's pool — SuperAdmin-only override
   *  enforced by the backend. Pass `all=true` (SuperAdmin-only) to return the
   *  union of every managed-accounts row across all admins — used to populate
   *  the View-as dropdown for SuperAdmins on baskets/allocations/export. */
  async list(opts?: { accountManagerUserId?: string; all?: boolean }): Promise<ManagedAccount[]> {
    const params = new URLSearchParams();
    if (opts?.all) {
      params.set('all', 'true');
    } else if (opts?.accountManagerUserId) {
      params.set('account_manager_user_id', opts.accountManagerUserId);
    }
    const qs = params.toString();
    const url = qs ? `/admin/managed-users?${qs}` : '/admin/managed-users';
    try {
      const response = await apiClient.get<ApiResponse<ManagedAccount[]>>(
        url,
        undefined,
        API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to list managed accounts');
      }
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to list managed accounts',
      );
    }
  }

  /** Detach a managed account. Without `accountManagerUserId`, detaches from
   *  the caller's own pool. With it, scopes to that AM's pool — SuperAdmin-only. */
  async detach(
    managedUserId: string,
    managedClientId: string,
    accountManagerUserId?: string,
  ): Promise<void> {
    const url = accountManagerUserId
      ? `/admin/managed-users/${managedUserId}/${managedClientId}?account_manager_user_id=${encodeURIComponent(accountManagerUserId)}`
      : `/admin/managed-users/${managedUserId}/${managedClientId}`;
    try {
      const response = await apiClient.delete<ApiResponse>(
        url,
        undefined,
        API_TYPE,
      );
      if (!response.success) {
        throw new Error(response.message || 'Failed to detach managed account');
      }
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to detach managed account',
      );
    }
  }
}

const managedAccountsService = new ManagedAccountsService();
export default managedAccountsService;
